import { Component, computed, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { BaseDialogComponent } from '../../../kit/base-dialog/base-dialog.component';
import { SelectInputComponent } from '../../../kit/input/select-input/select-input.component';
import { PersonDependenceDialogStore } from './person-dependence-dialog.store';
import { fb } from 'packages/client/src/app/modules/shared/shared.global-service';
import { Validators } from '@angular/forms';
import { SaveButtonComponent } from '../../../kit/button/save-button.component';
import { TableColumn } from 'packages/client/src/app/modules/shared/components/kit/table/table.types';
import { dateField } from 'packages/client/src/app/modules/shared/components/kit/table/table.fields';
import { FilterBuilder } from 'packages/client/src/app/modules/content/helper/filter.builder';
import { contentGlobalSuccess } from 'packages/client/src/app/modules/content/content.error';
import { dateify } from 'packages/client/src/app/modules/shared/helpers/date.helper';
import { dependenceFrag } from './person-dependence-dialog.query';
import { TranslatePipe } from '@ngx-translate/core';
import {
    PersonDependenceCreateInput,
    PersonDependenceUpdateInput,
} from '@libs/types/src/services/input-type/social/person-dependence';
import { PersonDependenceStateEnum } from '@libs/types/src/services/enum/person-dependence.enum';
import { CancelButtonComponent } from '../../../kit/button/cancel-button.component';
import { DateInputComponent } from '../../../kit/input/date-input/date-input.component';
import { CardComponent } from '../../../kit/card/card.component';
import { AppFormGroup, changeSignal } from '../../../../utils/form.utils';
import { CardTableComponent } from '../../../../modules/shared/components/kit/table/card-table.component';
import { FilterInput } from '@it2go/data-grid';
import { ConfirmBuilder } from '../../../../modules/shared/service/confirm.builder';
import { PrimeTemplate } from '@openng/optimus-ui/api';

@Component({
    selector: 'app-person-dependence-dialog',
    templateUrl: 'person-dependence-dialog.component.html',
    providers: [PersonDependenceDialogStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SelectInputComponent,
        SaveButtonComponent,
        TranslatePipe,
        CancelButtonComponent,
        DateInputComponent,
        CardComponent,
        CardTableComponent,
        PrimeTemplate,
    ],
})
export class PersonDependenceDialogComponent extends BaseDialogComponent {

    public readonly store = inject(PersonDependenceDialogStore);
    public readonly confirm = inject(ConfirmBuilder);

    protected form = new AppFormGroup({
        id: fb.control<number | null>(null),
        from: fb.control<Date | null>(null, Validators.required),
        state: fb.control<number | null>(null, Validators.required),
        degree: fb.control<number | null>(null),
    });

    protected prefix = 'person.dependence.';

    protected headerTitle = computed(() => {
        const groupId = this.formIdChanged();
        if (groupId) {
            return `${this.prefix}editDependence`;
        } else {
            return `${this.prefix}newDependence`;
        }
    });

    protected columns: TableColumn[] = [
        dateField('from'),
        {
            name: 'state',
        },
        {
            name: 'degree',
        },
    ];

    protected additionalFilters = computed<FilterInput[][]>(() => {
        return FilterBuilder.singlePage({
            personId: this.personId,
        }).filter || [];
    });

    private personId = this.dialogConfig.data.personId;
    private dependenceTable = viewChild<CardTableComponent>(CardTableComponent);
    private formIdChanged = changeSignal<number | null>(this.form.controls.id);

    protected updateRecord(record: any) {
        this.form.patchValue({
            id: record.id,
            from: dateify(record.from),
            state: record.state,
            degree: record?.dependence?.degree ?? null,
        });
    }

    protected async deleteRecord(event: any) {
        this.confirm.confirmDelete(
            async () => {
                this.store.api.deletePersonDependence({ id: event.item.id }).then(() => {
                    this.dependenceTable()?.reload();
                    contentGlobalSuccess.next('common.deleted');
                });
            },
            event,
        );
    }

    protected async submit() {
        if (this.form.isInvalid()) return;

        const data: Record<string, any> = {
            from: this.form.value.from!.toISOString(),
            state: this.form.value.state,
            degree: this.form.value.degree,
        };

        if (data.state === PersonDependenceStateEnum.NOT_REQUESTED) {
            data.degree = null;
        }

        if (this.form.value.id) {
            data.id = this.form.value.id;
            this.store.api.updatePersonDependence(data as PersonDependenceUpdateInput, 'id').then(() => {
                this.form.reset();
                this.dependenceTable()?.reload();
                contentGlobalSuccess.next('common.updated');
            });
        } else {
            data.personId = this.personId;
            this.store.api.createPersonDependence(data as PersonDependenceCreateInput, 'id').then(() => {
                this.form.reset();
                this.dependenceTable()?.reload();
                contentGlobalSuccess.next('common.assigned');
            });
        }
    }

    protected readonly PersonDependenceStateEnum = PersonDependenceStateEnum;
    protected readonly dependenceFrag = dependenceFrag;
}
